<script lang="ts" setup>
import api from "@/api";
import {
	type TTag,
	type TContractType,
	type TFieldOfStudyApi,
	type TFieldOfStudyInfo,
} from "@/assets/js/globalTypes";
import {
	debounce,
	defaultNumberLocale,
	remoteWorkOptions,
	useCurrency,
	useIsMobile,
	yearsOfExperience,
} from "@/assets/js/helpers";
import { $t } from "@/i18n";
import { useProfileStore } from "@/store/profile";
import { useToastStore } from "@/store/toast";
import type { SelectChangeEvent, SelectFilterEvent } from "primevue/select";
import type { TreeNode } from "primevue/treenode";

definePage({
	name: "teacher.profile.teaching-info",
	meta: {
		returnToTop: true,
	},
});

const toastStore = useToastStore();
const contractTypes = ref<TContractType[]>([]);
async function getContractTypes() {
	try {
		const res = await api.getContractTypes();
		contractTypes.value = res.data.data;
	} catch (error) {
		console.error(error);
	}
}

const profileStore = useProfileStore();
const currency = useCurrency();
const filteredSkills = ref<TTag[]>([]);
const expandedKeys = ref<{ [key: string]: boolean }>({});

function selectContractTypes(e: SelectChangeEvent) {
	if (e.value.includes("FRLN")) {
		const hasFRLN = profileStore.profileChanges.contract_types.some(
			(contract) => contract.ctt_code === "FRLN",
		);
		if (!hasFRLN) {
			toastStore.openToastInfo($t("app.teacher.profile.missingDocumentsInfo"));
		}
	}

	const selectedContractTypes = contractTypes.value.filter((contract) =>
		e.value.includes(contract.ctt_code),
	);
	profileStore.profileChanges.contract_types = selectedContractTypes;
}

const fieldsOfStudyFlat = ref<TFieldOfStudyInfo[]>([]);

const fieldsOfStudy = ref<TreeNode[]>([]);
async function getFieldsOfStudy() {
	const handleItem = (item: TFieldOfStudyApi) => {
		if (item.selectable) {
			fieldsOfStudyFlat.value.push({
				sfp_id: item.sfp_id,
				sfp_name: item.sfp_name,
			});
		}
		const obj: TreeNode = {
			key: item.sfp_id + "",
			data: item,
			label: item.sfp_name,
			selectable: item.selectable,
			children: [],
		};
		if (item.childs) {
			for (const el of item.childs) {
				obj.children.push(handleItem(el));
			}
		}
		return obj;
	};
	try {
		const res = await api.getFieldsOfStudy();
		if (res.data.data) {
			for (const item of res.data.data as TFieldOfStudyApi[]) {
				const obj = handleItem(item);
				fieldsOfStudy.value.push(obj);
			}
			// expandAll(fieldsOfStudy.value);
		}
	} catch (error) {
		console.error(error);
	}
}

const defaultSelectedNodes = computed(() => {
	if (!profileStore.profileChanges.subjects) return null;
	const res: { [key: string]: boolean } = {};
	for (const el of profileStore.profileChanges.subjects || []) {
		res[el.sfp_id] = true;
	}
	return res;
});

function handleFosSelect(e) {
	const selectedNodes = e;
	const res = [];
	for (const el of Object.keys(selectedNodes)) {
		const selectedSubjects = fieldsOfStudyFlat.value.find(
			(subject) => subject.sfp_id === parseInt(el),
		);
		res.push(selectedSubjects);
	}

	profileStore.profileChanges.subjects = res;
}

const skillsSearch = ref("");
const skills = ref<TTag[]>([]);
async function getSkills() {
	// let ignore_pagination: "1" | "" = "1";
	// if (!skillsSearch.value) {
	// skills.value = [];
	// ignore_pagination = "1";
	// return;
	// }
	// console.log(ignore_pagination);
	try {
		const res = await api.getAllSkills({
			tag_name: skillsSearch.value,
			// count: 999,
			ignore_pagination: "1",
		});
		skills.value = res.data.data;
		filteredSkills.value = res.data.data;
	} catch (error) {
		console.error(error);
	}
}

const skillOptions = computed(() => {
	const res = [...skills.value];
	const uniqueSkills = new Map();
	for (const skill of [...(profileStore.profileChanges.skills || []), ...res]) {
		uniqueSkills.set(skill.tag_id, skill);
	}
	return Array.from(uniqueSkills.values());
});
// const getSkillsDebounced = debounce(getSkills, 500, false);

// function handleSkillSearch(e: SelectFilterEvent) {
// 	skillsSearch.value = e.value;
// 	getSkillsDebounced();
// }

const handleSkillSearch = debounce(
	(e: SelectFilterEvent) => {
		skillsSearch.value = e.value;
		filteredSkills.value = skillsSearch.value
			? skills.value.filter((skill) =>
					skill.tag_name
						.toLowerCase()
						.includes(skillsSearch.value.toLowerCase()),
				)
			: skills.value;
	},
	300,
	false,
);

function handleSkillSelect(e: SelectChangeEvent) {
	const selectedSkills = skillOptions.value.filter((skill) =>
		e.value.includes(skill.tag_id),
	);
	profileStore.profileChanges.skills = selectedSkills;
}

const isMobile = useIsMobile();

const hasPendingValidation = computed(() => {
	const pendingColumns = profileStore.profilePending?.columns ?? [];
	const keysToCheck = ["usr_hourlyrate"] as const;

	return keysToCheck.some((key) => pendingColumns.includes(key));
});

onMounted(async () => {
	await getContractTypes();
	await getFieldsOfStudy();
	await getSkills();
});

// function expandAll(nodes: TreeNode[]) {
// 	for (const node of nodes) {
// 		if (node.children && node.children.length > 0) {
// 			expandedKeys.value[node.key] = true;
// 			expandAll(node.children);
// 		}
// 	}
// }

// onMounted(() => {
// 	document.body.classList.add("has-tree-select");
// });

// onUnmounted(() => {
// 	document.body.classList.remove("has-tree-select");
// });
</script>

<template>
	<div id="my-profile-teaching-info">
		<div
			v-if="isMobile"
			class="sub-title-profile-page"
		>
			{{ $t("app.base.step") }} 2 |
			{{ $t("app.teacher.profileStepper.teaching") }}
			<span v-tooltip.bottom="$t('app.teacher.profile.pendingValidation')">
				<i-material-symbols:alarm-outline-rounded v-if="hasPendingValidation" />
			</span>
		</div>
		<info-icon
			class="help-icon-profile-page"
			:text="$t('app.teacher.profile.teachingInfoHelperText')"
		/>
		<div
			v-if="isMobile"
			class="teacher-profile-fake-circle"
		></div>
		<div class="content-wrapper">
			<div class="top-wrapper">
				<div class="section-wrapper teaching-requirements-wrapper">
					<div class="my-profile-section-title">
						{{ $t("app.teacher.profile.teachingRequirement") }}
						<span class="my-profile-required-fields">
							* - {{ $t("app.base.requiredFields") }}
						</span>
					</div>
					<div class="form-wrapper">
						<div class="form-left-wrapper">
							<p-float-label
								:pt="{
									root: {
										class: 'my-profile-float-label-root',
									},
								}"
							>
								<p-input-number
									id="profile-usr_hourlyrate"
									v-model="profileStore.profileChanges.usr_hourlyrate"
									v-tooltip.bottom="
										profileStore.isFieldPending('columns', 'usr_hourlyrate')
											? $t('app.teacher.profile.pendingValidation')
											: ''
									"
									mode="currency"
									:min="0"
									:locale="defaultNumberLocale"
									:currency="currency.iso"
									:max-fraction-digits="0"
									:class="{
										'pending-validation': profileStore.isFieldPending(
											'columns',
											'usr_hourlyrate',
										),
									}"
									:invalid="profileStore.profileChanges.usr_hourlyrate > 999"
									fluid
									:pt="{
										root: {
											class: 'my-profile-input-root',
										},
									}"
								/>
								<label
									class="my-profile-input-label"
									for="profile-usr_hourlyrate"
								>
									{{ $t("app.teacher.hourlyRate") }} *
								</label>
							</p-float-label>
							<p-float-label
								:pt="{
									root: {
										class: 'my-profile-float-label-root',
									},
								}"
							>
								<p-multi-select
									v-if="contractTypes.length"
									id="profile-contract_type"
									v-model="profileStore.contractChangesCodes"
									:options="contractTypes"
									option-value="ctt_code"
									option-label="ctt_name"
									:show-toggle-all="false"
									fluid
									:pt="{
										root: {
											class: 'my-profile-input-root',
										},
									}"
									@change="selectContractTypes"
								/>
								<label
									v-if="contractTypes.length"
									class="my-profile-input-label"
									for="profile-contract_type"
								>
									{{ $t("app.teacher.contractType", 3) }} *
								</label>
								<div
									v-if="!contractTypes.length"
									class="my-profile-input-root"
								>
									<p-progress-spinner></p-progress-spinner>
								</div>
							</p-float-label>
						</div>
						<div class="form-right-wrapper">
							<p-float-label
								:pt="{
									root: {
										class: 'my-profile-float-label-root',
									},
								}"
							>
								<p-select
									id="profile-usr_remotework"
									v-model="profileStore.profileChanges.usr_remotework"
									:options="remoteWorkOptions"
									option-label="label"
									option-value="value"
									fluid
									:pt="{
										root: {
											class: 'my-profile-input-root',
										},
									}"
								></p-select>
								<label
									class="my-profile-input-label"
									for="profile-usr_remotework"
								>
									{{ $t("app.teacher.remoteWork") }}
								</label>
							</p-float-label>
						</div>
					</div>
				</div>
				<div class="section-wrapper experience-wrapper">
					<div class="my-profile-section-title">
						{{ $t("app.teacher.experience") }}
					</div>
					<div class="form-wrapper">
						<p-float-label
							:pt="{
								root: {
									class: 'my-profile-float-label-root',
								},
							}"
						>
							<p-select
								id="profile-usr_experience"
								v-model="profileStore.profileChanges.usr_experience"
								:options="yearsOfExperience"
								option-label="label"
								option-value="value"
								fluid
								:pt="{
									root: {
										class: 'my-profile-input-root',
									},
								}"
							></p-select>
							<label
								class="my-profile-input-label"
								for="profile-usr_experience"
							>
								{{ $t("app.teacher.teachingExperience") }} *
							</label>
						</p-float-label>
						<p-float-label
							:pt="{
								root: {
									class: 'my-profile-float-label-root',
								},
							}"
						>
							<p-select
								id="profile-usr_professionalexperience"
								v-model="profileStore.profileChanges.usr_professionalexperience"
								:options="yearsOfExperience"
								option-label="label"
								option-value="value"
								fluid
								:pt="{
									root: {
										class: 'my-profile-input-root',
									},
								}"
							></p-select>
							<label
								class="my-profile-input-label"
								for="profile-usr_professionalexperience"
							>
								{{ $t("app.teacher.professionalExperience") }} *
							</label>
						</p-float-label>
					</div>
				</div>
			</div>
			<div class="bottom-wrapper">
				<div
					v-if="fieldsOfStudy.length"
					class="section-wrapper subjects-wrapper"
				>
					<div class="my-profile-section-title">
						{{ $t("app.base.subjects", 2) }}
					</div>
					<div class="select-wrapper">
						<p-tree-select
							:options="fieldsOfStudy"
							:default-value="defaultSelectedNodes"
							selection-mode="multiple"
							filter
							:placeholder="$t('app.teacher.selectSubjects') + ' *'"
							:selected-items-label="$t('app.teacher.selectSubjects') + ' *'"
							:max-selected-labels="0"
							:pt="{
								root: {
									class: [
										'my-profile-input-root',
										'my-profile-tree-select-root',
									],
								},
							}"
							:expanded-keys="expandedKeys"
							@change="handleFosSelect"
						></p-tree-select>
						<div class="select-wrapper-chips-wrapper">
							<p-chip
								v-for="subject in profileStore.profileChanges.subjects"
								:key="subject.sfp_id"
								removable
								:pt="{
									root: {
										class: 'my-profile-chip-root',
									},
								}"
								@remove="
									profileStore.profileChanges.subjects.splice(
										profileStore.profileChanges.subjects.findIndex(
											(el) => el.sfp_id === subject.sfp_id,
										),
										1,
									)
								"
							>
								{{ subject.sfp_name }}
							</p-chip>
							<p-button
								v-if="profileStore.profileChanges.subjects?.length"
								:pt="{
									root: {
										class: ['clear-all-btn'],
									},
								}"
								variant="text"
								@click="profileStore.profileChanges.subjects = []"
							>
								<i-ic-outline-restart-alt />
								<span>{{ $t("app.base.clear", 2) }}</span>
							</p-button>
						</div>
					</div>
				</div>
				<div
					v-else
					class="section-wrapper subjects-wrapper"
				>
					<p-progress-spinner></p-progress-spinner>
				</div>
				<div class="section-wrapper skills-wrapper">
					<div class="my-profile-section-title">
						{{ $t("app.tags.skls", 2) }}
					</div>
					<div class="select-wrapper">
						<p-multi-select
							id="profile-skills"
							v-model="profileStore.skillChangesIds"
							option-label="tag_name"
							:show-toggle-all="false"
							option-value="tag_id"
							filter
							:empty-selection-message="$t('app.base.noItem')"
							:empty-filter-message="$t('app.base.noResults')"
							:empty-message="$t('app.base.noAvailableOptions')"
							filter-match-mode="contains"
							:auto-filter-focus="true"
							:placeholder="$t('app.teacher.selectSkills', { number: 15 })"
							:selected-items-label="
								$t('app.teacher.selectSkills', { number: 15 })
							"
							:max-selected-labels="0"
							:selection-limit="15"
							fluid
							:pt="{
								root: {
									class: 'my-profile-input-root',
								},
							}"
							:options="filteredSkills"
							:virtual-scroller-options="{ itemSize: 38 }"
							@filter="handleSkillSearch"
							@change="handleSkillSelect"
						>
							<template #header>
								<div class="dropdown-header-text">
									{{
										$t(
											"app.base.pleaseSearch",
											{
												item: $t("app.tags.skls", 2).toLowerCase(),
											},
											3,
										)
									}}
								</div>
							</template>
						</p-multi-select>
						<div class="select-wrapper-chips-wrapper">
							<p-chip
								v-for="subject in profileStore.profileChanges.skills"
								:key="subject.tag_id"
								removable
								:pt="{
									root: {
										class: 'my-profile-chip-root',
									},
								}"
								@remove="
									profileStore.profileChanges.skills.splice(
										profileStore.profileChanges.skills.findIndex(
											(el) => el.tag_id === subject.tag_id,
										),
										1,
									)
								"
							>
								{{ subject.tag_name }}
							</p-chip>
							<p-button
								v-if="profileStore.profileChanges.skills?.length"
								:pt="{
									root: {
										class: ['clear-all-btn'],
									},
								}"
								variant="text"
								@click="profileStore.profileChanges.skills = []"
							>
								<i-ic-outline-restart-alt />
								<span>{{ $t("app.base.clear", 2) }}</span>
							</p-button>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<style lang="scss" scoped>
$inputs-gap-sm: 30px;
$section-gap-sm: $inputs-gap-sm;
$content-gap-sm: 30px;
$content-gap-md: 48px;

// body {
// 	border: 10px solid red;
// 	// :deep() {
// 	// 	.p-tree-node-children {
// 	// 		background-color: rgb(255 0 0);
// 	// 		padding-inline-start: 0 !important;
// 	// 	}
// 	// }
// }

#my-profile-teaching-info {
	// width: 100%;

	.content-wrapper {
		background-color: $daidai-light-blue;
		padding: 0 17px 30px;
		// max-width: 100%;

		.top-wrapper {
			margin-bottom: $content-gap-sm;
		}

		.top-wrapper,
		.bottom-wrapper {
			display: flex;
			flex-direction: column;
			gap: $content-gap-sm;
			// max-width: 100%;

			.section-wrapper {
				display: flex;
				flex-direction: column;
				gap: $section-gap-sm;
				// max-width: 100%;

				.form-wrapper {
					gap: $inputs-gap-sm;

					.form-left-wrapper,
					.form-right-wrapper {
						display: flex;
						flex-direction: column;
						gap: $inputs-gap-sm;
					}
				}

				.select-wrapper {
					gap: 17px;
					// max-width: 100%;

					// :deep() {
					// 	.tree-container {
					// 		.p-tree-node-children {
					// 			background-color: rgb(255 0 0);
					// 		}
					// 	}
					// }
				}

				.select-wrapper,
				.form-wrapper {
					display: flex;
					flex-direction: column;
				}
			}
		}
	}
}

@media screen and (width >= 768px) {
	#my-profile-teaching-info {
		.content-wrapper {
			padding: 24px 0 37px;
			background-color: unset;
			max-width: 500px;

			.top-wrapper {
				margin-bottom: $content-gap-md;
			}

			.top-wrapper,
			.bottom-wrapper {
				display: flex;
				flex-direction: column;
				gap: $content-gap-md;
			}
		}
	}
}

@media screen and (width >= 1400px) {
	#my-profile-teaching-info {
		padding: 16px 57px 16px 23px;

		.content-wrapper {
			max-width: unset;

			.top-wrapper {
				margin-bottom: 40px;
			}

			.top-wrapper,
			.bottom-wrapper {
				display: flex;
				flex-direction: row;
				gap: 32px;
				box-sizing: border-box;

				.teaching-requirements-wrapper {
					width: calc(((100% - 32px * 2) / 3 * 2) + 32px);
					box-sizing: border-box;

					.form-wrapper {
						display: flex;
						flex-direction: row;
						gap: 32px;
						box-sizing: border-box;

						.form-left-wrapper,
						.form-right-wrapper {
							flex: 1;
							box-sizing: border-box;
						}
					}
				}

				.experience-wrapper,
				.skills-wrapper,
				.subjects-wrapper {
					width: calc((100% - 32px * 2) / 3);
					box-sizing: border-box;
				}
			}
		}
	}
}
</style>
